// @flow
/**
 * WebApi - back-endiga suhtlemise klass. Kasutamine:
 * 
 * WebApi = require('/api/WebApi');
 * WebApi
 *  	.get(table, id)
 *  	.then(function (response) {
 *  		// Päring õnnestus, parameetri response ülesehitus vt allpool
 *  	})
 *  	.catch(function (reason) {
 *  		// Juhtus viga, parameetri reason ülesehitus on järgnev:
 *  		// reason.response - response objekt (eksisteerib juhul, kui tegemist serveri vastusega indikeeritud veaga)
 *  		// reason.message - veateate tekst, täidetud juhul, kui response objekti pole
 *  	});
 *  
 *  Response objekt on järgmise struktuuriga:
 *  response = {
 *  	data - serveri poolt tagastatud data
 *  	status - serveri poolt tagastatud staatuskood
 *  	statusText - serveri poolt tagastatud staatuskoodile järgnenud tekst
 *  	headers - serveri poolt tagastatud päised objektina
 *  	config - päringu konfiguratsioon
 *  }
 *  
 * Kasutada saab järgmisi meetodeid:
 * 
 *  WebApi.url(url)
 *  WebApi.authorization(token)
 *  
 *  WebApi.find(table, queryObject)
 *  WebApi.get(table, id)
 *  WebApi.put(table, id, object)
 *  WebApi.post(table, object)
 *  WebApi.delete(table, id)
 * 
 */


import axios from 'axios';
import _ from "underscore";
import rootStore from "./stores";

//import NotificationActions from './actions/NotificationActions';

class WebApi {
	static url(baseUrl) {
		axios.defaults.baseURL = baseUrl;
	}
	
	static authorization(token) {
		axios.defaults.headers.common['Authorization'] = token;
	}
	
	static handleError(error) {
		if (error.response) {
			console.log('Error', 'Response status: ' + error.response.status.toString());
		} else {
			// Internal error:
			console.log('Error', error.message);
		}
		throw error;
	}
	
	static getResource(url, query, config = null) {
		let c = _.defaults({params: query}, config);
		if (!c.auth) c.headers = {"Authorization": "Bearer " + rootStore.sessionStore.getToken()};
		return axios.get(url, c).catch(WebApi.handleError);
	}

	static putResource(url, data, config = null) {
		let c = _.defaults({}, config);
		if (!c.auth) c.headers = {"Authorization": "Bearer " + rootStore.sessionStore.getToken()};
		return axios.put(url, {data: data}, c).catch(WebApi.handleError);
	}
	
	static postResource(url, data, config = null) {
		let c = _.defaults({}, config);
		if (!c.auth) c.headers = {"Authorization": "Bearer " + rootStore.sessionStore.getToken()};
		return axios.post(url, {data: data}, c).catch(WebApi.handleError);
	}
	
	static deleteResource(url, config = null) {
		let c = _.defaults({}, config);
		if (!c.auth) c.headers = {"Authorization": "Bearer " + rootStore.sessionStore.getToken()};
		return axios.delete(url, c).catch(WebApi.handleError);
	}

	static getData(url, config = null) {
		let c = _.defaults({responseType: 'text'}, config);
		if (!c.auth) c.headers = {"Authorization": "Bearer " + rootStore.sessionStore.getToken()};
		return axios.get(url, c).catch(WebApi.handleError);
	}

	static putData(url, data, config = null) {
		let c = _.defaults({
			headers: {'Content-Type': 'text/plain'}
		}, config);
		if (!c.auth) c.headers["Authorization"] = "Bearer " + rootStore.sessionStore.getToken();
		return axios.put(url, data, c).catch(WebApi.handleError);
	}
	
	/**
	 * Toote lugemine
	 * @param {string} code Toote kood
	 */
	static getProduct(code) {
		return this.getResource('/products/' + code);
	}
	
	/**
	 * Toote salvestamine
	 * @param {string} code Toote kood
	 * @param {object} data Toote andmed
	 */
	static postProduct(code, data) {
		return this.postResource('/products/' + code, data);
	}
	
	static deleteProduct(code) {
		return this.deleteResource('/products/' + code);
	}
	
	/**
	 * NCP faili lugemine
	 * @param {string} code Toote kood
	 * @param {string} type Faili tüüp (kas 'ncp' või 'dat')
	 */
	static getNcp(code, type) {
		return this.getData('/ncp/' + code + "." + type);
	}
	
	/**
	 * NCP faili salvestamine
	 * @param {string} code Toote kood
	 * @param {string} type Faili tüüp (kas 'ncp' või 'dat')
	 * @param {string} data Toote andmed
	 */
	static putNcp(code, type, data) {
		return this.putData('/ncp/' + code + "." + type, data);
	}

	static getConfig() {
		return this.getResource("/configuration", null);
	}

	static putConfig(data) {
		return this.putResource("/configuration", data);
	}

	static getActiveConfig() {
		return this.getResource("/configuration/active", null);
	}

	static getSchema(schema) {
		return this.getResource("../schema/" + encodeURIComponent(schema) + ".json").then(function (result) {
			result.schema = schema;
			return result;
		}).catch(function (error) {
			error.schema = schema;
			throw error;
		});
	}

	static getSoftwareInfo(user, passwd) {
		return this.getResource("/firmware/info", null, {auth: {username: user, password: passwd}});
	}

	static postSoftwareUpdate(file, user, passwd) {
		return this.postResource("/firmware/update", {file: file}, {auth: {username: user, password: passwd}});
	}

	static postSoftwareRestore(user, passwd) {
		return this.postResource("/firmware/restore", null, {auth: {username: user, password: passwd}});
	}

	static getDiagnostics() {
		return this.getResource("/diagnostics", null);
	}

	static createQueryString(obj) {
		let str = [];
		for(let p in obj) {
			if (obj.hasOwnProperty(p)) {
				str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
			}
		}
		return str.join("&");
	}
}


export default WebApi;
